import { createElement, useMemo } from "react";

import { UIView, useCurrentStateAndParams } from "@uirouter/react";
import { ErrorBoundary } from "react-error-boundary";

import { useTranslate } from "@similarweb/i18n-provider";
import { Button, EmptyState } from "@similarweb/ui-components";

import { ErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyState";
import { WithHasAccessToSegment } from "app/pages/industry-analysis/frame/WithHasAccessToSegment";
import { WithIsEmptyIndustry } from "app/pages/industry-analysis/frame/WithIsEmptyIndustry";
import { WithRedirectToMarketResearchOverview } from "app/pages/industry-analysis/frame/WithRedirectToMarketResearchOverview";
import { WithSegmentAndDomainsOverlapped } from "app/pages/industry-analysis/frame/WithSegmentAndDomainsOverlapped";
import { WithSegmentsLoader } from "app/pages/industry-analysis/frame/WithSegmentsLoader";
import { WithSegmentsLoading } from "app/pages/industry-analysis/frame/WithSegmentsLoading";
import { shouldShowComparisonBlankState } from "app/pages/industry-analysis/routes/routeUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { pipe } from "app/UtilitiesAndConstants/UtilityFunctions/pipe";

import { V2_ROUTES } from "../routes/routes";

const EmptyStateCtaButton = () => {
    const { translate } = useTranslate();
    const { params } = useCurrentStateAndParams();

    return (
        <Button onClick={() => void proNavigator.go(V2_ROUTES.overview, { ...params })}>
            {translate("industry.research.comparison.page.notAvailable.cta")}
        </Button>
    );
};

export const IndustryResearchPage = ({ shouldLogErrorFallback }: { shouldLogErrorFallback: boolean }) => {
    const { translate } = useTranslate();
    const { params, state } = useCurrentStateAndParams();

    const showBlankState = shouldShowComparisonBlankState(state.name, params.category as string | string[] | undefined);

    // Each WithXxx HOC returns a new wrapped component on every call, so the composed
    // view must stay memoized to avoid remounting its subtree on every render.
    const IndustryResearchPageView = useMemo(() => {
        const industryResearchPageLogicWrapper = pipe(
            WithRedirectToMarketResearchOverview,
            WithSegmentAndDomainsOverlapped,
            WithHasAccessToSegment,
            WithIsEmptyIndustry,
            WithSegmentsLoader,
            WithSegmentsLoading,
        );
        return industryResearchPageLogicWrapper(UIView);
    }, []);

    return (
        <ErrorBoundary
            FallbackComponent={(props) => (
                <ErrorFallback
                    {...props}
                    shouldLogErrorFallback={shouldLogErrorFallback}
                />
            )}
            resetKeys={[state, params]}
        >
            {showBlankState ? (
                <EmptyState
                    title={translate("industry.research.comparison.page.notAvailable.title", {
                        pageTitle: translate(state.pageTitle as string),
                    })}
                    subtitle={translate("industry.research.comparison.page.notAvailable.subtitle")}
                    CtaButtons={EmptyStateCtaButton}
                />
            ) : (
                createElement(IndustryResearchPageView)
            )}
        </ErrorBoundary>
    );
};
